// src/sdk/bridge.js
//
// SDK-side half of the PointAI Try-On postMessage protocol. This runs INSIDE
// the iframe (the try-on app). The host page uses /pointai-tryon.js, which is
// the other half.
//
// Every message is a plain object with a `type` string prefixed `pointai:`.
//
//   Host → SDK
//     { type:'pointai:tryon',  productId, sku? }   show + select a product
//     { type:'pointai:select', productId, sku? }   select without changing visibility
//     { type:'pointai:show' }                      panel brought into view (resume)
//     { type:'pointai:hide' }                      panel moved out of view (pause)
//     { type:'pointai:ping' }                      → SDK replies 'pointai:ready' if booted
//
//   SDK → Host
//     { type:'pointai:loaded' }                    SDK page is up and listening (before camera)
//     { type:'pointai:ready' }                     tracker running, first frame rendered
//     { type:'pointai:status', text, ready }       status line changes
//     { type:'pointai:selected', productId, sku, name }
//     { type:'pointai:close' }                     user pressed the SDK's own close button
//     { type:'pointai:error', reason, message }
//
// Origin policy: by default the SDK accepts messages from any origin and posts
// back to the origin that first contacted it. The host loader passes
// ?host=shop.example (host name only — a full URL in the query string is
// rejected with 406 by common shared-hosting firewalls); ?origin=https://…
// is also accepted when set by hand. Either locks the SDK to that host.

const PREFIX = 'pointai:';

export class Bridge {
  constructor({ allowedOrigin = '*' } = {}) {
    this.allowedOrigin = allowedOrigin;
    // postMessage needs a full origin or '*'; a bare host name (?host=) is only
    // usable as a filter, so reply to '*' until the host's first message tells
    // us its exact origin.
    this._hostOrigin = /^https?:\/\//.test(allowedOrigin) ? allowedOrigin : '*';
    this._hostWindow = window.parent !== window ? window.parent : null;
    this._handlers = new Map();
    this.embedded = !!this._hostWindow;

    // Resolves the first time the host asks us to show / try on. main.js waits
    // on this in embed mode so the camera isn't requested while hidden.
    this.firstOpen = new Promise(res => { this._resolveFirstOpen = res; });

    window.addEventListener('message', (ev) => this._onMessage(ev));

    // Status events raised by ui.js are forwarded to the host.
    window.addEventListener('pointai:status', (ev) => {
      this.post('status', ev.detail || {});
    });
  }

  on(type, fn) {
    const list = this._handlers.get(type) || [];
    list.push(fn);
    this._handlers.set(type, list);
    return this;
  }

  post(type, payload = {}) {
    if (!this._hostWindow) return;
    try {
      this._hostWindow.postMessage({ type: PREFIX + type, ...payload }, this._hostOrigin);
    } catch (err) {
      console.warn('[bridge] post failed', err);
    }
  }

  _onMessage(ev) {
    const data = ev.data;
    if (!data || typeof data !== 'object' || typeof data.type !== 'string') return;
    if (!data.type.startsWith(PREFIX)) return;
    if (this.allowedOrigin !== '*' && !originAllowed(ev.origin, this.allowedOrigin)) {
      console.warn('[bridge] dropped message from disallowed origin', ev.origin);
      return;
    }
    // Reply to the exact origin that talks to us (it passed the filter above).
    if (ev.origin && ev.origin !== 'null') this._hostOrigin = ev.origin;
    if (ev.source && ev.source !== window) this._hostWindow = ev.source;

    const type = data.type.slice(PREFIX.length);
    if (type === 'tryon' || type === 'show') this._resolveFirstOpen?.(data);

    const list = this._handlers.get(type) || [];
    for (const fn of list) {
      try { fn(data); } catch (err) { console.error('[bridge] handler error', type, err); }
    }
  }
}

// `allowed` may be a full origin ("https://shop.example") or a bare host name
// ("shop.example", what the loader sends as ?host= — a full URL in the query
// string is blocked by many shared-hosting firewalls with a 406).
function originAllowed(origin, allowed) {
  if (origin === allowed) return true;
  try { return new URL(origin).hostname === allowed.replace(/^https?:\/\//, '').split('/')[0]; }
  catch (_) { return false; }
}

// Read the product id from a host message. Hosts may send either `productId`
// (their own id) or `sku`; both are accepted, `sku` wins if present.
export function skuFromMessage(msg) {
  return String(msg?.sku ?? msg?.productId ?? '').trim();
}
